import type { Metro, OccupationGroupAlignment } from '../types/metro';

/**
 * 22-group universe locked 2026-05-08 alongside Maria's `dummy_data_v1.xlsx`
 * (PR #23 backend ingest). Each entry: stable URL-friendly chip id ->
 * full display name.
 *
 * The previous 5-group set ('management', 'stem', 'healthcare',
 * 'protective', 'bluecollar') was inherited from the static prototype
 * and didn't match Maria's real occupational-group taxonomy — that
 * taxonomy has 22 groups, more granular than Healthcare → split into
 * practitioners/support, STEM → split into multiple, etc.
 *
 * Chip ids derived from the names by the `slugify` rule used in
 * `backend/scripts/ingest_excel.py` so frontend/backend stay in
 * lockstep on URL semantics. (Compute once at module load via
 * `slugifyGroupName` — keeps the source of truth in one place even
 * if Georgetown ships a 23rd group later.)
 */
export const ALL_OCCUPATIONAL_GROUPS: ReadonlyArray<string> = [
  'Architects, surveyors, drafters, and technicians',
  'Arts, design, entertainment, sports, and media',
  'Business operations',
  'Community and social services',
  'Computer and mathematical sciences',
  'Construction and extraction',
  'Education',
  'Farming, fishing, and forestry',
  'Financial operations',
  'Food preparation and serving',
  'Healthcare professional and technical',
  'Healthcare support',
  'Installation, maintenance, and equipment repair',
  'Legal',
  'Life and physical sciences',
  'Management',
  'Office and administrative support',
  'Personal care',
  'Production',
  'Protective services',
  'Sales',
  'Transportation and material moving',
] as const;

/**
 * Convert a group display name to a stable URL-safe chip id.
 *
 * Mirrors the Python `slugify` helper in `backend/scripts/ingest_excel.py`
 * so the same group ends up with the same id on both sides:
 *
 *   'Healthcare professional and technical' -> 'healthcare-professional-and-technical'
 *   'Architects, surveyors, drafters, and technicians'
 *     -> 'architects-surveyors-drafters-and-technicians'
 */
export function slugifyGroupName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Map from chip id → display name. Built once at module load from the
 * 22-name universe. Lookups via `lookupSectorByChipId(id)` so the
 * Record-with-undefined-values cast lives in exactly one place.
 */
export const OCC_CHIP_TO_SECTOR: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(
      ALL_OCCUPATIONAL_GROUPS.map((name) => [slugifyGroupName(name), name]),
    ),
  );

/**
 * Look up the display name for a chip id, returning `null` for both
 * the no-selection case (`chipId === null`) and the defensive case
 * (chip id present but not in the universe).
 */
export function lookupSectorByChipId(chipId: string | null): string | null {
  if (chipId === null || chipId === '') return null;
  const lookup = OCC_CHIP_TO_SECTOR as Record<string, string | undefined>;
  return lookup[chipId] ?? null;
}

/**
 * Returns true if the given metro should be included when the chip
 * `chipId` is selected. Semantics: "this metro CARRIES a row for the
 * named occupational group" — either status.
 *
 * CR-039 (Frank email 2026-09-17): the previous rule additionally
 * demanded `status === 'shortage'`, which silently excluded every
 * group-surplus metro from Map + Rankings under an occupational-group
 * filter ("all the credential-to-jobs values with a surplus have been
 * excluded") while Detail — which doesn't route through
 * `useFilteredMetros` — still showed the rows. Carrying the group is
 * the inclusion rule; status filtering is the status pills' job (see
 * `lensedStatusForMetro`). DEC-081.
 *
 * Returns true when `chipId` is null (no filter) or when the chip id
 * doesn't map to a known group (defensive — keeps the metro visible
 * if the chip set drifts from the universe).
 *
 * Reads from `metro.occupationGroups` (real data from the API).
 * Returns true unconditionally when the metro has no
 * `occupationGroups` array (or an empty array — older test fixtures
 * predating PR #23) so legacy code paths that haven't migrated still
 * see the full metro list rather than silently filtering everything
 * out.
 */
export function metroMatchesOccChip(
  metro: Pick<Metro, 'occupationGroups'>,
  chipId: string | null,
): boolean {
  if (chipId === null) return true;
  const sectorName = lookupSectorByChipId(chipId);
  if (!sectorName) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[occupation-utils] Unknown chip id "${chipId}" — chip set has drifted from OCC_CHIP_TO_SECTOR.`,
      );
    }
    return true;
  }
  const groups = metro.occupationGroups;
  if (!groups || groups.length === 0) return true;
  const match: OccupationGroupAlignment | undefined = groups.find(
    (g) => g.name === sectorName,
  );
  // No row for this group in this metro → metro doesn't have the
  // group at all (subset semantics) → exclude. A row with EITHER
  // status → include (CR-039).
  return match !== undefined;
}

/**
 * The alignment status a metro should be CLASSIFIED under given the
 * active occupational-group lens: the selected group's own status when
 * the metro carries that group's row, otherwise the metro-level status.
 *
 * CR-039 companion to the `metroMatchesOccChip` widening: under a lens
 * the dot/row the user sees renders the GROUP figure (via
 * `lensedRatioForMetro`, CR-06), so the status pills must classify by
 * that same figure — otherwise a metro-level-shortage metro whose
 * selected group is in surplus would vanish from a "surplus" pill
 * filter while displaying a surplus-colored value. Same fallback shape
 * as `lensedRatioForMetro`: no lens / unknown chip / metro doesn't
 * carry the group / no `occupationGroups` at all → metro-level status.
 */
export function lensedStatusForMetro(
  metro: Pick<Metro, 'status' | 'occupationGroups'>,
  occupationalGroupId: string | null,
): Metro['status'] {
  const lensSectorName = lookupSectorByChipId(occupationalGroupId);
  if (!lensSectorName) return metro.status;
  const hit = metro.occupationGroups?.find((g) => g.name === lensSectorName);
  return hit ? hit.status : metro.status;
}

/**
 * Result of lensing a metro's headline ratio to a selected occupational-
 * group filter.
 */
export interface LensedRatio {
  /** Ratio to render: the OG-specific ratio when the metro carries the
   *  selected group, otherwise the metro's overall ratio. */
  readonly ratio: number;
  /**
   * True only when `ratio` above is genuinely the OG-specific figure
   * (an OG filter is active AND the metro carries that group's row).
   * False in both the "no filter active" case and the "metro doesn't
   * carry this group" fallback case — callers must not label `ratio`
   * as OG-scoped (e.g. an "in {group}" qualifier) when this is false,
   * or they'd be claiming precision the number doesn't have.
   */
  readonly isLensed: boolean;
  /**
   * Display name of the selected occupational group, or null when no
   * OG filter is active. Populated even when `isLensed` is false (the
   * metro-missing-group fallback case) so callers can still show
   * which group was requested if they want to render a caveat.
   */
  readonly lensSectorName: string | null;
}

/**
 * Single source of truth for "should this metro's headline ratio show
 * the metro-wide figure or the selected occupational group's figure?"
 *
 * CR-06 (2026-07-08, GX5): `MetroBubble` already lensed its bubble
 * color/size to the selected OG filter, but `MetroTooltip` and
 * `MetroPopup` kept rendering `metro.ratio` (the overall figure) in
 * their headlines — so hovering/clicking a metro under an OG filter
 * showed a DIFFERENT number than the bubble underneath it. All three
 * components now call this one helper instead of each re-deriving the
 * "find this metro's row for the selected group" lookup, so the
 * lensing logic can't drift between them again.
 *
 * Falls back to the metro's overall ratio (with `isLensed: false`)
 * when there's no active OG filter, or when the metro's
 * `occupationGroups` doesn't carry a row for the selected group
 * (partial coverage) — in both cases the map should still tell a
 * story rather than rendering nothing.
 */
export function lensedRatioForMetro(
  metro: Pick<Metro, 'ratio' | 'occupationGroups'>,
  occupationalGroupId: string | null,
): LensedRatio {
  const lensSectorName = lookupSectorByChipId(occupationalGroupId);
  if (!lensSectorName) {
    return { ratio: metro.ratio, isLensed: false, lensSectorName: null };
  }
  const hit = metro.occupationGroups?.find((g) => g.name === lensSectorName);
  if (!hit) {
    return { ratio: metro.ratio, isLensed: false, lensSectorName };
  }
  return { ratio: hit.ratio, isLensed: true, lensSectorName };
}
